import { useCallback, useMemo } from 'react';

import { SettingsGearIcon } from '@braskai/icons';
import { useLocation, useNavigate } from 'react-router-dom';

import type { PageViewSourceState } from '@/shared/lib/pageViewSource';
import type { NavigationItemProps } from '@/shared/ui/NavigationItem';

import { useTeamspaceSettingsModal } from '@/features/teamspace';

import { useBalanceStore } from '@/entities/balance';
import { useCanUseRecording, useScreenRecordAction } from '@/entities/recording';
import { useCurrentSpaceId } from '@/entities/teamspace';
import { useFeatureFlags } from '@/entities/user/currentUser';

import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { useInterfaceLanguage } from '@/shared/i18n';
import { inIframe } from '@/shared/lib/browser';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { DesktopSidebarNavigation } from '@/shared/ui/DesktopNavigation';
import { Logo } from '@/shared/ui/Logo';
import { SidebarButton } from '@/shared/ui/SidebarButton';

import { getHelpNavOptions, getNavOptions, getSpacePath, getToolsNavOptions } from '../../lib/getNavOptions';
import { useShowViewPlans } from '../../model/useShowViewPlans';
import { ProfileItem } from '../ProfileItem';
import { TeamspacesNav } from '../TeamspacesNav';
import { ViewPlansButton } from '../ViewPlansButton';

type DesktopNavProps = {
  isOpen?: boolean;
  locked?: boolean;
  handleCloseSidebar?: () => void;
  handleLocked?: () => void;
};

export const DesktopNav = ({ isOpen, locked, handleCloseSidebar, handleLocked }: DesktopNavProps) => {
  const { t } = useInterfaceLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { featureVisible: isVoicePresetsFeatureVisible } = useFeatureFlags('voice_presets');
  const { featureVisible: isGlossaryFeatureVisible } = useFeatureFlags('glossary');
  const openTeamspaceSettingsModal = useTeamspaceSettingsModal((state) => state.open);
  const planCategory = useBalanceStore((state) => state.planCategory);
  const hasTrialPlan = useBalanceStore((state) => state.hasTrialPlan);
  const canUseRecording = useCanUseRecording();
  const openScreenRecord = useScreenRecordAction();

  const currentSpaceId = useCurrentSpaceId();
  const showViewPlans = useShowViewPlans();

  const handleNewTranslation = useCallback(() => {
    const state: { newTranslation: true } & PageViewSourceState = { newTranslation: true, source: 'plus' };

    navigate(getSpacePath(PATHS.translation, currentSpaceId), { state });
    handleCloseSidebar?.();
  }, [currentSpaceId, handleCloseSidebar, navigate]);

  const handleNewRecording = useCallback(() => {
    MixpanelEvents.newRecordingProjectSelected({ entry_point: 'plus' });
    void openScreenRecord();
    handleCloseSidebar?.();
  }, [handleCloseSidebar, openScreenRecord]);

  const mainNavigation = getNavOptions({
    isRecordingVisible: canUseRecording,
    pathname,
    spaceId: currentSpaceId,
    t,
    onNewRecording: handleNewRecording,
    onNewTranslation: handleNewTranslation,
  });

  const toolsNavigation = getToolsNavOptions({
    isGlossaryFeatureVisible,
    isVoicePresetsFeatureVisible,
    pathname,
    spaceId: currentSpaceId,
    t,
  });

  const handleSettingsClick = useCallback(() => {
    MixpanelEvents.settingsAndMembersClick();
    openTeamspaceSettingsModal();
    handleCloseSidebar?.();
  }, [handleCloseSidebar, openTeamspaceSettingsModal]);

  const settingsItem = useMemo<NavigationItemProps>(
    () => ({
      title: t('sidebar.settingsMembers'),
      dataTestId: 'sidebar-menu-settings',
      icon: <SettingsGearIcon height={16} width={16} />,
      onClick: handleSettingsClick,
    }),
    [handleSettingsClick, t],
  );

  const helpNavigation = getHelpNavOptions({
    hasTrialPlan,
    isWhiteLabel: IS_WHITE_LABEL,
    planCategory,
    t,
    settingsItem,
  });

  const opened = Boolean(isOpen) || Boolean(locked);

  const isTiktok = inIframe();

  return (
    <DesktopSidebarNavigation
      logoContainer={
        <Logo
          imageHeight={22}
          isCompact={!opened}
          isWhiteLabel={IS_WHITE_LABEL}
          padding="18px 16px 12px"
          target={isTiktok ? undefined : '_blank'}
          to={isTiktok ? '/' : 'https://www.rask.ai/'}
        >
          <SidebarButton data-testid="desktop-nav-locked" locked={locked} onClick={handleLocked} />
        </Logo>
      }
      helpNavigation={helpNavigation}
      isOpen={opened}
      mainNavigation={mainNavigation}
      profileContainer={<ProfileItem active={pathname === PATHS.account} />}
      promoContainer={showViewPlans && opened ? <ViewPlansButton onNavigate={handleCloseSidebar} /> : undefined}
      teamspaceContainer={<TeamspacesNav opened={opened} handleCloseSidebar={handleCloseSidebar} />}
      toolsNavigation={toolsNavigation}
      toolsTitle={t('sidebarNav.assets')}
    />
  );
};
